import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { authClient, useAuth, formatApiError } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import SectionHeader from "@/components/SectionHeader";
import { sfx } from "@/lib/sounds";
import { MessageSquare, Send, ArrowLeft, Check, CheckCheck, Reply, Edit2, Trash2, Smile, X as XIcon } from "lucide-react";

function timeAgo(ts) {
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ""; }
}

const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉", "😮", "🔥"];

function PresenceDot({ online }) {
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]" : "bg-white/25"}`}
      title={online ? "Online" : "Offline"}
      data-testid={`presence-dot-${online ? "on" : "off"}`}
    />
  );
}

function TypingIndicator({ visible }) {
  if (!visible) return null;
  return (
    <div className="flex items-center gap-1 text-xs text-white/50 px-3 py-1" data-testid="dm-typing">
      <span className="inline-flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" />
        <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0.1s" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0.2s" }} />
      </span>
      typing…
    </div>
  );
}

function MessageBubble({ m, mine, otherId, onReact, onReply, onEdit, onDelete }) {
  const [showActions, setShowActions] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const isRead = (m.read_by || []).includes(otherId);

  if (m.deleted) {
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
        <div className="max-w-[70%] rounded-2xl px-3 py-2 bg-white/5 border border-white/10 text-white/40 italic text-sm">
          message deleted
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex group ${mine ? "justify-end" : "justify-start"}`}
      data-testid={`dm-msg-${m.id}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowEmoji(false); }}
    >
      <div className={`relative max-w-[70%] rounded-2xl px-3 py-2 ${
        mine ? "bg-[#FFBF00]/15 border border-[#FFBF00]/30 text-white"
             : "bg-white/[0.04] border border-white/10 text-white/90"
      }`}>
        {m.reply_to && (
          <Link to="#" onClick={(e) => e.preventDefault()} className="block mb-1.5 pl-2 border-l-2 border-[#FFBF00]/40 text-[11px] text-white/50">
            <span className="text-[#FFBF00]">↳ {m.reply_to.sender_name}</span>: {m.reply_to.body}
          </Link>
        )}
        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
        {m.images && m.images.length > 0 && (
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            {m.images.slice(0, 4).map((src, i) => <img key={i} src={src} alt="" className="rounded-md object-cover" />)}
          </div>
        )}
        {/* Reactions */}
        {Object.keys(m.reactions || {}).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {Object.entries(m.reactions).map(([emoji, users]) => (
              <button key={emoji} onClick={() => onReact(m.id, emoji)}
                      data-testid={`dm-reaction-${m.id}-${emoji}`}
                      className="text-xs px-1.5 py-0.5 rounded-full bg-black/40 border border-white/10 hover:border-[#FFBF00]/40">
                {emoji} <span className="text-white/50">{users.length}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-white/40 mt-1">
          <span>{timeAgo(m.created_at)}</span>
          {m.edited_at && <span className="text-white/30">· edited</span>}
          {mine && (isRead
            ? <CheckCheck size={11} className="text-[#3ABEFF]" data-testid={`dm-read-${m.id}`} />
            : <Check size={11} className="text-white/50" />
          )}
        </div>

        {/* Action toolbar */}
        {showActions && (
          <div className={`absolute -top-8 ${mine ? "right-0" : "left-0"} flex items-center gap-1 bg-black/80 backdrop-blur border border-white/10 rounded-full px-1.5 py-1`}>
            <button onClick={() => setShowEmoji((s) => !s)} data-testid={`dm-react-btn-${m.id}`} className="p-1 hover:bg-white/10 rounded-full" title="React"><Smile size={12} /></button>
            <button onClick={() => onReply(m)} data-testid={`dm-reply-btn-${m.id}`} className="p-1 hover:bg-white/10 rounded-full" title="Reply"><Reply size={12} /></button>
            {mine && <button onClick={() => onEdit(m)} data-testid={`dm-edit-btn-${m.id}`} className="p-1 hover:bg-white/10 rounded-full" title="Edit"><Edit2 size={12} /></button>}
            {mine && <button onClick={() => onDelete(m)} data-testid={`dm-delete-btn-${m.id}`} className="p-1 hover:bg-red-500/10 text-red-400 rounded-full" title="Delete"><Trash2 size={12} /></button>}
            {showEmoji && (
              <div className={`absolute top-full mt-1 ${mine ? "right-0" : "left-0"} bg-black/90 border border-white/10 rounded-full px-2 py-1 flex gap-1`}>
                {REACTION_EMOJIS.map((emoji) => (
                  <button key={emoji} onClick={() => { onReact(m.id, emoji); setShowEmoji(false); }}
                          data-testid={`dm-emoji-${m.id}-${emoji}`}
                          className="text-base hover:scale-125 transition-transform">{emoji}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Thread({ convId, onBack, refreshInbox }) {
  const { user } = useAuth();
  const rt = useRealtime();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const listRef = useRef();
  const typingTimerRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await authClient.get(`/messages/${convId}`);
      setConv(data.conversation);
      setMsgs(data.messages);
      // Notify server that we've read via WS
      rt.send("read", { conv_id: convId });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [convId, rt]);

  useEffect(() => { load(); }, [load]);

  // Probe presence for the other participant (REST) when WS map has no entry
  useEffect(() => {
    const other = conv?.other_user;
    if (!other?.id) return;
    if (!rt.presence.get(other.id)) rt.probePresence(other.id);
  }, [conv?.other_user, rt]);

  // Subscribe to WS events for this conversation
  useEffect(() => {
    if (!convId) return () => {};
    const unsubs = [];
    unsubs.push(rt.subscribe("message", (m) => {
      if (m.conversation_id !== convId) return;
      setMsgs((prev) => prev.some(x => x.id === m.message.id) ? prev : [...prev, m.message]);
      // If message is from other, mark as read since we're viewing
      if (m.message.sender_id !== user?.id) rt.send("read", { conv_id: convId });
      refreshInbox?.();
    }));
    unsubs.push(rt.subscribe("typing", (m) => {
      if (m.conv_id !== convId || m.user_id === user?.id) return;
      setOtherTyping(true);
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => setOtherTyping(false), 3000);
    }));
    unsubs.push(rt.subscribe("read", (m) => {
      if (m.conv_id !== convId) return;
      setMsgs((prev) => prev.map(x => x.read_by?.includes(m.user_id) ? x : ({ ...x, read_by: [...(x.read_by || []), m.user_id] })));
    }));
    unsubs.push(rt.subscribe("reaction", (m) => {
      if (m.conv_id !== convId) return;
      setMsgs((prev) => prev.map(x => x.id === m.message_id ? { ...x, reactions: m.reactions } : x));
    }));
    unsubs.push(rt.subscribe("edit", (m) => {
      if (m.conv_id !== convId) return;
      setMsgs((prev) => prev.map(x => x.id === m.message_id ? { ...x, body: m.body, edited_at: m.edited_at } : x));
    }));
    unsubs.push(rt.subscribe("delete", (m) => {
      if (m.conv_id !== convId) return;
      setMsgs((prev) => prev.map(x => x.id === m.message_id ? { ...x, deleted: true, body: "", images: [] } : x));
    }));
    return () => unsubs.forEach(u => u());
  }, [convId, rt, user?.id, refreshInbox]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, otherTyping]);

  const sendTyping = () => rt.send("typing", { conv_id: convId });

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      if (editing) {
        await authClient.put(`/messages/${convId}/messages/${editing.id}`, { body: text });
      } else {
        await authClient.post(`/messages/${convId}`, { body: text, reply_to_id: replyTo?.id || null });
      }
      setText(""); setReplyTo(null); setEditing(null);
      sfx.click();
      refreshInbox?.();
    } catch (e) {
      sfx.error();
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const react = async (msgId, emoji) => {
    try {
      await authClient.post(`/messages/${convId}/messages/${msgId}/react`, { emoji });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteMsg = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await authClient.delete(`/messages/${convId}/messages/${m.id}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!conv) return <div className="p-8 text-center text-white/50">Loading…</div>;
  const other = conv.other_user || {};
  const isOnline = rt.isOnline(other.id);
  const lastSeen = rt.lastSeen(other.id);
  return (
    <div className="flex flex-col h-[70vh] rounded-2xl glass overflow-hidden" data-testid="dm-thread">
      <div className="flex items-center gap-3 p-4 border-b border-white/10">
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-white/5 md:hidden"><ArrowLeft size={16} /></button>
        <Link to={`/users/${other.id}`} onClick={() => sfx.click()} className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative">
            <img src={other.avatar} alt="" className="w-10 h-10 rounded-lg" />
            <div className="absolute -bottom-0.5 -right-0.5 border-2 border-black rounded-full">
              <PresenceDot online={isOnline} />
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{other.name}</div>
            <div className="text-[11px] text-white/50 truncate">
              {isOnline ? <span className="text-emerald-400">Online</span>
                : (lastSeen ? `last seen ${timeAgo(lastSeen)}` : (other.mc_username || ""))}
            </div>
          </div>
        </Link>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="dm-messages">
        {msgs.map((m) => (
          <MessageBubble
            key={m.id} m={m} mine={m.sender_id === user?.id} otherId={other.id}
            onReact={react} onReply={setReplyTo} onEdit={(msg) => { setEditing(msg); setText(msg.body); }} onDelete={deleteMsg}
          />
        ))}
        <TypingIndicator visible={otherTyping} />
        {msgs.length === 0 && <div className="text-center text-white/40 text-sm py-16">Say hello 👋</div>}
      </div>
      {(replyTo || editing) && (
        <div className="flex items-center gap-2 px-4 py-2 bg-white/[0.03] border-t border-white/10 text-xs">
          <span className="text-[#FFBF00]">{editing ? "Editing" : "Replying to"}</span>
          <span className="truncate flex-1 text-white/60">{(editing || replyTo)?.body}</span>
          <button onClick={() => { setReplyTo(null); setEditing(null); setText(""); }} className="p-1 hover:bg-white/10 rounded-full"><XIcon size={12} /></button>
        </div>
      )}
      <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-white/10">
        <input
          value={text} onChange={(e) => { setText(e.target.value); sendTyping(); }}
          placeholder={editing ? "Edit message…" : "Type a message…"}
          maxLength={4000}
          data-testid="dm-input"
          className="flex-1 bg-black/50 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFBF00]/40"
        />
        <button type="submit" disabled={busy || !text.trim()} data-testid="dm-send"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50">
          <Send size={13} /> {editing ? "Save" : "Send"}
        </button>
      </form>
    </div>
  );
}

export default function Messages() {
  const { user, loading } = useAuth();
  const rt = useRealtime();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const openId = params.get("c");
  const [inbox, setInbox] = useState([]);

  useEffect(() => { if (!loading && !user) nav("/"); }, [loading, user, nav]);

  const load = useCallback(async () => {
    try { const { data } = await authClient.get("/messages/inbox"); setInbox(data); }
    catch (err) { console.warn("inbox load failed", err?.message); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  // Refresh inbox on any conv_update or new message
  useEffect(() => {
    if (!user) return () => {};
    const un1 = rt.subscribe("conv_update", () => load());
    const un2 = rt.subscribe("message", () => load());
    return () => { un1(); un2(); };
  }, [user, rt, load]);

  if (!user) return null;

  return (
    <div className="pt-32 pb-16 px-4 sm:px-6" data-testid="page-messages">
      <div className="max-w-6xl mx-auto">
        <SectionHeader eyebrow="Direct Messages" title="Your inbox" subtitle="Private conversations across the network." />
        {!rt.connected && (
          <div className="mb-3 text-[11px] text-white/40 uppercase tracking-widest">● Reconnecting to realtime…</div>
        )}
        <div className="grid md:grid-cols-[320px_1fr] gap-4">
          <aside className="rounded-2xl glass overflow-hidden h-[70vh] flex flex-col" data-testid="dm-inbox">
            <div className="p-4 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Inbox · {inbox.length}</div>
            <div className="flex-1 overflow-y-auto">
              {inbox.length === 0 && (
                <div className="p-8 text-center text-white/40 text-sm">
                  <MessageSquare className="mx-auto mb-2 opacity-40" />
                  No conversations yet.<br/>Open any user profile to start one.
                </div>
              )}
              {inbox.map((c) => (
                <button key={c.id}
                        onClick={() => setParams({ c: c.id })}
                        data-testid={`dm-item-${c.id}`}
                        className={`w-full text-left flex items-center gap-3 p-3 border-b border-white/5 hover:bg-white/5 ${
                          openId === c.id ? "bg-[#FFBF00]/10" : ""
                        }`}>
                  <div className="relative">
                    <img src={c.other_user?.avatar} alt="" className="w-11 h-11 rounded-lg" />
                    <div className="absolute -bottom-0.5 -right-0.5 border-2 border-black rounded-full">
                      <PresenceDot online={rt.isOnline(c.other_user?.id)} />
                    </div>
                    {c.unread && <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-[#FFBF00] shadow-[0_0_8px_rgba(255,191,0,0.6)]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`flex items-baseline gap-2 ${c.unread ? "text-white" : "text-white/70"}`}>
                      <span className="font-semibold truncate">{c.other_user?.name || "User"}</span>
                      <span className="ml-auto text-[10px] text-white/40 whitespace-nowrap">{c.last_message_at ? timeAgo(c.last_message_at) : ""}</span>
                    </div>
                    <div className={`text-[11px] truncate ${c.unread ? "text-white/80" : "text-white/40"}`}>
                      {c.last_sender_id === user.id ? "You: " : ""}{c.last_message_preview || "…"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>
          <div>
            {openId ? (
              <Thread convId={openId} onBack={() => setParams({})} refreshInbox={load} />
            ) : (
              <div className="rounded-2xl glass p-12 h-[70vh] grid place-items-center text-white/50 text-sm">
                Pick a conversation to start.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
